// Smart Sash Admin Panel - Stream Management Hook
// Manages active sessions list, filtering, pagination, and auto-refresh

import { ActiveSessionInfo } from '@/models/teaching.models';
import { useAuth } from '@/services/auth';
import { teachingOrchestrationService } from '@/services/grpc/teachingOrchestrationService';
import { useLabStore, useStreamStore, useUIStore } from '@/store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 10;
const AUTO_REFRESH_INTERVAL = 30_000;
const GRPC_URL = import.meta.env.VITE_GRPC_URL || 'https://localhost:5001';

export const useStreamManagement = () => {
  const {
    sessions,
    totalCount,
    totalPages,
    currentPage: serverPage,
    isLoading,
    error,
    accessChecks,
    fetchSessions,
    checkAccess,
    setActiveSession,
    clearError,
  } = useStreamStore();

  const { labs, fetchLabs } = useLabStore();
  const { addNotification } = useUIStore();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedLabId, setSelectedLabId] = useState<string>('');
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const institutionId = user?.institutionId ?? '';
  const userId = user?.id ?? '';

  const loadSessions = useCallback(async () => {
    if (!institutionId || !userId) return;

    try {
      await fetchSessions({
        institutionId,
        userId,
        labId: selectedLabId || undefined,
        pageNumber: currentPage,
        pageSize,
      });
    } catch {
      // Error handled by store
    }
  }, [institutionId, userId, selectedLabId, currentPage, pageSize, fetchSessions]);

  // Check access for all sessions when sessions change
  useEffect(() => {
    if (!institutionId || !userId || sessions.length === 0) return;

    const uncheckedDeviceIds = [
      ...new Set(
        sessions.map((s) => s.deviceId).filter((deviceId) => deviceId && !accessChecks[deviceId])
      ),
    ];

    uncheckedDeviceIds.forEach((deviceId) => {
      checkAccess(institutionId, userId, deviceId);
    });
  }, [sessions, institutionId, userId, accessChecks, checkAccess]);

  // Initialize gRPC service
  useEffect(() => {
    teachingOrchestrationService.initialize(GRPC_URL);
  }, []);

  // Load sessions on mount and when filters change
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load labs for filter dropdown
  useEffect(() => {
    fetchLabs(1, 100);
  }, [fetchLabs]);

  // Auto-refresh
  useEffect(() => {
    refreshTimerRef.current = setInterval(loadSessions, AUTO_REFRESH_INTERVAL);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [loadSessions]);

  const handleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    loadSessions();
    refreshTimerRef.current = setInterval(loadSessions, AUTO_REFRESH_INTERVAL);
  }, [loadSessions]);

  const handleLabFilterChange = useCallback((labId: string) => {
    setSelectedLabId(labId);
    setCurrentPage(1);
  }, []);

  const handleJoin = useCallback(
    (session: ActiveSessionInfo) => {
      const access = accessChecks[session.deviceId];
      if (access && !access.canJoin) {
        addNotification({
          type: 'warning',
          message: access.reason || 'You do not have access to join this session',
          duration: 3000,
        });
        return;
      }
      setActiveSession(session);
      navigate(`/streams/${session.streamSessionId}`);
    },
    [accessChecks, setActiveSession, navigate, addNotification]
  );

  return {
    sessions,
    totalCount,
    totalPages,
    currentPage: serverPage || currentPage,
    isLoading,
    error,
    labs,
    selectedLabId,
    accessChecks,
    setCurrentPage,
    handleLabFilterChange,
    handleRefresh,
    handleJoin,
    clearError,
  };
};
